import { OptionsApi } from '@core/swagger-client';
import { ClientType, LiveStatus, WebinarStatus } from '@core/types';
import { ErrorMessagePage } from '@core/ui/src/components/ErrorMessagePage';
import { QuestionnaireOverlay } from '@online-webinar/player';
import { useNavigate, useParams } from '@online-webinar/router';
import {
    joinWebinar,
    leaveWebinar,
    onDemandJoinWebinar,
    onDemandLeaveWebinar,
    startQuestionnaire,
    useAppDispatch,
    useAppSelector
} from '@online-webinar/store';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

export const ExamPage = () => {
    const { webinarId, linkCode } = useParams({ strict: false });
    const nav = useNavigate();
    const dispatch = useAppDispatch();

    const { data: webinarStatus, isLoading: isLoadingWebinarStatus } = useQuery(OptionsApi.webinar.getStatus(webinarId!));

    const onDemandIsInitialized = useAppSelector((state) => state.onDemandWebinar.isInitialized);
    const onDemandExam = useAppSelector((state) => state.onDemandWebinar.exam);
    const onDemandExamAnswers = useAppSelector((state) => state.onDemandWebinar.examAnswers);
    const onDemandDataConnection = useAppSelector((state) => state.onDemandWebinar.dataConnection);

    const liveIsInitialized = useAppSelector((state) => state.liveWebinar.isInitialized);
    const liveExam = useAppSelector((state) => state.liveWebinar.exam);
    const liveExamAnswers = useAppSelector((state) => state.liveWebinar.examAnswers);
    const liveDataConnection = useAppSelector((state) => state.liveWebinar.dataConnection);

    const isInitialized = webinarStatus === WebinarStatus.Ended ? onDemandIsInitialized : liveIsInitialized;
    const exam = webinarStatus === WebinarStatus.Ended ? onDemandExam : liveExam;
    const examAnswers = webinarStatus === WebinarStatus.Ended ? onDemandExamAnswers : liveExamAnswers;
    const dataConnection = webinarStatus === WebinarStatus.Ended ? onDemandDataConnection : liveDataConnection;

    // Since we need the participant information, we need to join the webinar
    // We join the on-demand webinar so we do not skew live-webinar data (and view time for on-demand webinars is not recorded when video isn't playing)
    useEffect(() => {
        if (isLoadingWebinarStatus) return;

        if (webinarStatus === WebinarStatus.Ended) {
            dispatch(onDemandJoinWebinar({ webinarId: webinarId!, linkCode: linkCode, clientType: ClientType.on_demand }));
            return () => {
                dispatch(onDemandLeaveWebinar());
            };
        }

        if (webinarStatus === WebinarStatus.Planned || webinarStatus === WebinarStatus.Started) {
            dispatch(joinWebinar({ webinarId: webinarId!, linkCode: linkCode, clientType: ClientType.viewer }));
            return () => {
                dispatch(leaveWebinar());
            };
        }
    }, [isLoadingWebinarStatus, webinarStatus]);

    useEffect(() => {
        if (!isInitialized) return;
        if (!exam) {
            nav({ to: '/$webinarId', params: { webinarId: webinarId! } });
            return;
        }
        if (!exam.enabled || !exam.isValid) {
            nav({ to: '/$webinarId', params: { webinarId: webinarId! } });
            return;
        }

        dispatch(startQuestionnaire({ type: 'exam', questionnaire: exam, answers: examAnswers }));
    }, [isInitialized, exam, examAnswers]);

    if (dataConnection.status === LiveStatus.Error) {
        const error = dataConnection.error ?? { statusCode: 500, success: false };
        return <ErrorMessagePage apiError={error} />;
    }

    return (
        <>
            <QuestionnaireOverlay
                onClose={() => {
                    nav({ to: '/$webinarId', params: { webinarId: webinarId! } });
                }}
            />
        </>
    );
};
